/**
 * Hindernisse auf dem Dach: Kamin, Dachfenster, Gaube, Lüfter (PV-D07).
 *
 * Alles hier rechnet in der Dachebene — x längs der Traufe, vom Boden aus
 * gesehen nach rechts, y von der Traufe zum First. So misst auch der Monteur:
 * „60 auf 60, 2,40 von der Traufe, 1,80 vom linken Ortgang“. Das Luftbild gibt
 * die Lage nur auf einen halben Meter her (Wunsch von Mathias, 11/2026).
 *
 * Gespeichert bleibt der Ring. Maß, Drehung und Abstände werden aus ihm
 * gelesen und schreiben ihn neu — es gibt keine zweite Wahrheit daneben.
 */
import { huellkasten, dreheRing } from './polygon.js';
import { gradInBogen, bogenInGrad } from './typen.js';
/** Winkel auf (−45, 45] bringen — ein Rechteck sieht alle 90 Grad gleich aus. */
function aufViertel(grad) {
    let w = ((grad % 90) + 90) % 90;
    if (w > 45)
        w -= 90;
    return w === 0 ? 0 : w;
}
/**
 * Drehung gegen die Traufe, gemessen an der längsten Kante.
 *
 * Bei einem gezeichneten Viereck ist die längste Kante die verlässlichste:
 * Kurze Kanten sind auf dem Luftbild die ungenauesten.
 */
export function drehungZurTraufeGrad(ring) {
    let laengste = 0;
    let winkel = 0;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const l = Math.hypot(b.x - a.x, b.y - a.y);
        if (l > laengste) {
            laengste = l;
            winkel = bogenInGrad(Math.atan2(b.y - a.y, b.x - a.x));
        }
    }
    return aufViertel(winkel);
}
/**
 * Maß in der eigenen Lage: erst zurückdrehen, dann den Kasten nehmen. Sonst
 * misst ein um 20 Grad gedrehtes Dachfenster ein Viertel zu breit.
 */
export function hindernismass(ring) {
    if (ring.length < 3)
        return null;
    const drehungGrad = drehungZurTraufeGrad(ring);
    const um = huellkasten(ring);
    const drehpunkt = { x: (um.minX + um.maxX) / 2, y: (um.minY + um.maxY) / 2 };
    const gerade = dreheRing(ring, -gradInBogen(drehungGrad), drehpunkt);
    const k = huellkasten(gerade);
    const mitteGerade = { x: (k.minX + k.maxX) / 2, y: (k.minY + k.maxY) / 2 };
    const [mitte] = dreheRing([mitteGerade], gradInBogen(drehungGrad), drehpunkt);
    return { breiteM: k.maxX - k.minX, hoeheM: k.maxY - k.minY, drehungGrad, mitte: mitte };
}
/** Rechteck um eine Mitte, gedreht gegen die Traufe. */
export function hindernisRechteck(mitte, breiteM, hoeheM, drehungGrad = 0) {
    const b = breiteM / 2;
    const h = hoeheM / 2;
    const gerade = [
        { x: mitte.x - b, y: mitte.y - h },
        { x: mitte.x + b, y: mitte.y - h },
        { x: mitte.x + b, y: mitte.y + h },
        { x: mitte.x - b, y: mitte.y + h },
    ];
    return drehungGrad === 0 ? gerade : dreheRing(gerade, gradInBogen(drehungGrad), mitte);
}
/** Um die eigene Mitte drehen, Form und Maß bleiben. */
export function dreheHindernis(ring, umGrad) {
    const k = huellkasten(ring);
    return dreheRing(ring, gradInBogen(umGrad), { x: (k.minX + k.maxX) / 2, y: (k.minY + k.maxY) / 2 });
}
export function verschiebeRing(ring, dx, dy) {
    return ring.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}
/**
 * Wie weit ist der nächste Rand, von der Mitte aus geradeaus?
 *
 * Gemessen wird wie mit dem Maßband: von der Kante des Hindernisses
 * waagrecht zum Ortgang, senkrecht zu Traufe und First — und zwar auf Höhe des
 * Hindernisses, nicht am Hüllrechteck des Dachs. Bei einem Walmdach läge der
 * Ortgang sonst dort, wo das Dach gar nicht hinreicht.
 */
export function randabstaende(dach, hindernis) {
    const k = huellkasten(hindernis);
    const mitte = { x: (k.minX + k.maxX) / 2, y: (k.minY + k.maxY) / 2 };
    const halbB = (k.maxX - k.minX) / 2;
    const halbH = (k.maxY - k.minY) / 2;
    const strahl = (d, halb) => {
        const t = ersterSchnitt(dach, mitte, d);
        return t === null ? null : t - halb;
    };
    return {
        traufeM: strahl({ x: 0, y: -1 }, halbH),
        firstM: strahl({ x: 0, y: 1 }, halbH),
        linksM: strahl({ x: -1, y: 0 }, halbB),
        rechtsM: strahl({ x: 1, y: 0 }, halbB),
    };
}
/** Kleinster Abstand t ≥ 0, an dem der Strahl p + t·d eine Kante trifft. */
function ersterSchnitt(ring, p, d) {
    let bestes = null;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const ex = b.x - a.x;
        const ey = b.y - a.y;
        const nenner = d.x * ey - d.y * ex;
        if (Math.abs(nenner) < 1e-12)
            continue;
        const t = ((a.x - p.x) * ey - (a.y - p.y) * ex) / nenner;
        const s = ((a.x - p.x) * d.y - (a.y - p.y) * d.x) / nenner;
        if (t >= 0 && s >= -1e-9 && s <= 1 + 1e-9 && (bestes === null || t < bestes))
            bestes = t;
    }
    return bestes;
}
/**
 * Kleinste Fläche, die als Hindernis zählt: 10 auf 10 cm.
 *
 * Für Dachflächen gilt ein Quadratmeter — darunter ist es ein Zeichenfehler.
 * Ein Kamin hat aber 0,25 m², ein Dachfenster 0,9: Mit der Grenze der
 * Dachflächen blieb „Fertig“ beim Hindernis gesperrt, und es sah aus, als
 * ließe sich gar nicht zeichnen (Rückmeldung von Mathias, 11/2026).
 */
export const MINDESTFLAECHE_HINDERNIS_M2 = 0.01;
//# sourceMappingURL=hindernis.js.map